import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { logActivity } from "../utils/activityLogApi";

/**
 * ManualRisk — exact original design extracted from SecurityModel.jsx
 *
 * Props:
 *  userId                   - cognito username
 *  profile                  - auth profile (for orgId)
 *  userData                 - full userData object
 *  clientIP                 - IP string for activity logging
 *  manualRisks              - array state (lifted to parent)
 *  setManualRisks           - setter (lifted to parent)
 *  setProcessedManualRisks  - setter (lifted to parent)
 *  showToastMessage         - (message, variant) helper from parent
 *  getUserRegion            - () => string helper from parent
 */

// ── Utility ──────────────────────────────────────────────────────
const formatRelativeTime = (isoString) => {
  if (!isoString) return "just now";
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const ManualRisk = ({
  userId,
  profile,
  userData,
  clientIP,
  manualRisks,
  setManualRisks,
  setProcessedManualRisks,
  showToastMessage,
  getUserRegion,
}) => {
  // ── Local state ──────────────────────────────────────────────────
  const [addingManualRisk, setAddingManualRisk] = useState(false);
  const [loadingManualRisks, setLoadingManualRisks] = useState(false);
  const [manualInputFiles, setManualInputFiles] = useState([]);
  const [manualFileErrors, setManualFileErrors] = useState([]);
  const [manualRiskNotes, setManualRiskNotes] = useState("");

  const manualFileInputRef = useRef(null);
  const orgId = profile?.orgId;

  // ── Fetch on mount ───────────────────────────────────────────────
  useEffect(() => {
    if (!userId) return;
    const fetchManualRisks = async () => {
      setLoadingManualRisks(true);
      try {
        const res = await axios.get(
          "https://fxir4qtvb0.execute-api.ap-southeast-2.amazonaws.com/prod/api/manual-risks",
          {
            params: { userId, orgId: profile?.orgId },
            headers: { Region: getUserRegion() },
          }
        );
        const risks = res.data?.entries || res.data?.risks || [];
        setManualRisks(risks);
      } catch (err) {
        console.error("Failed to fetch manual risks", err);
      } finally {
        setLoadingManualRisks(false);
      }
    };
    fetchManualRisks();
  }, [userId]);

  // ── Helpers ──────────────────────────────────────────────────────
  const handleManualInputFileChange = (e) => {
    const selected = Array.from(e.target.files);
    const MAX = 10 * 1024 * 1024;
    const errors = [];
    const valid = selected.filter((f) => {
      if (f.size > MAX) {
        errors.push(`${f.name} exceeds 10 MB`);
        return false;
      }
      return true;
    });
    setManualFileErrors(errors);
    setManualInputFiles((prev) => [...prev, ...valid]);
    e.target.value = "";
  };

  const handleSaveToQueue = async () => {
    if (!manualRiskNotes.trim() && manualInputFiles.length === 0) {
      showToastMessage("Please enter text or attach at least one file.", "warning");
      return;
    }
    setAddingManualRisk(true);
    try {
      const fd = new FormData();
      fd.append("userId", userId);
      fd.append("orgId", profile?.orgId || "default");
      fd.append("text", manualRiskNotes.trim());
      fd.append("createdAt", new Date().toISOString());
      manualInputFiles.forEach((f) => fd.append("files", f));

      const res = await axios.post(
        "https://6n3kvfdjsg.execute-api.ap-southeast-2.amazonaws.com/prod/api/manual-risks/queue",
        fd,
        { headers: { Region: getUserRegion() } }
      );

      const saved = res.data?.risk || {
        riskId: `MR-${Date.now()}`,
        risk: manualRiskNotes.trim(),
        notes: manualRiskNotes.trim(),
        fileNames: manualInputFiles.map((f) => f.name),
        source: "Manual",
        enrichmentStatus: "PENDING",
        addedBy: userData?.email,
        createdAt: new Date().toISOString(),
      };

      setManualRisks((prev) => [...prev, saved]);
      setManualRiskNotes("");
      setManualInputFiles([]);
      setManualFileErrors([]);

      showToastMessage(
        "Entry saved to queue. Will be processed on next Commit & Recalculate.",
        "success"
      );
    logActivity({
                  userData,
                  orgId:       orgId,
                  region:      sessionStorage.getItem('userRegion') || 'AP',
                  module:      'Dashboard',
                  category:    'Manual-Risk',
                  action:      "Manual Risk Queued",
                  description: `ManualRisk is added in the queue`,
                ipAddress:   clientIP});
    } catch (err) {
      console.error("Failed to save queue entry", err);
      showToastMessage("Failed to save entry. Please try again.", "danger");
    } finally {
      setAddingManualRisk(false);
    }
  };

  const deleteManualRisk = async (riskId) => {
    try {
      await axios.delete(
        "https://fxir4qtvb0.execute-api.ap-southeast-2.amazonaws.com/prod/api/manual-risks",
        {
          data: { userId, orgId: profile?.orgId, riskId },
          headers: { Region: getUserRegion() },
        }
      );
      setManualRisks((prev) => prev.filter((r) => r.riskId !== riskId));
       logActivity({
                  userData,
                  orgId:       orgId,
                  region:      sessionStorage.getItem('userRegion') || 'AP',
                  module:      'Dashboard',
                  category:    'Manual-Risk',
                  action:      'Manual Risk Deleted',
                  description: `ManualRisk ${riskId} is deleted by ${userId}`,
                ipAddress:   clientIP});
    } catch (err) {
      showToastMessage("Failed to delete risk.", "danger");
    }
  };

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div style={{ marginLeft: 15 }}>
      <div className="p-3">

        {/* ── 1. Section Header ───────────────────────────────────── */}
        <div style={{ marginBottom: 20 }}>
          <h5 style={{
            fontSize: "1.45rem", fontWeight: 700,
            color: "#024A82", marginBottom: 4, fontFamily: "Inter, sans-serif",
          }}>
            Manual Risk Entry
          </h5>
          <p style={{
            fontSize: 13.5, color: "#475569", margin: 0,
            fontFamily: "Inter, sans-serif",
          }}>
            Describe anything — incident reports, vendor summaries. AI extracts
            structured risks on the next run in{" "}
            <strong style={{ color: "#035EA5" }}>Central Risk Dashboard</strong>.
          </p>
        </div>

        {/* ── 2. Input Card ───────────────────────────────────────── */}
        <div style={{
          background: "#FFFFFF",
          border: "1px solid #E2E8F0",
          borderRadius: 14,
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          padding: "18px 18px 14px",
          marginBottom: 16,
        }}>

          {/* Textarea */}
          <textarea
            rows={6}
            placeholder={
              "e.g. Our admin portal has no rate limiting on the login endpoint. " +
              "During last week's pen-test, the auditor demonstrated a brute-force attack " +
              "in 90 s. We also found three service accounts with no password expiry..."
            }
            value={manualRiskNotes}
            onChange={(e) => setManualRiskNotes(e.target.value)}
            style={{
              width: "100%",
              resize: "vertical",
              minHeight: 130,
              fontSize: 13.5,
              fontFamily: "Inter, sans-serif",
              color: "#1E293B",
              background: "#FAFCFF",
              border: "1px solid #CBD5E1",
              borderRadius: 10,
              padding: "12px 14px",
              lineHeight: 1.6,
              outline: "none",
              transition: "border-color 0.15s, box-shadow 0.15s",
              boxSizing: "border-box",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#035EA5";
              e.target.style.boxShadow = "0 0 0 3px #E6F0F9";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#CBD5E1";
              e.target.style.boxShadow = "none";
            }}
          />

          {/* Example chips + char counter row */}
         

          {/* Divider */}
          <div style={{ borderTop: "1px solid #F1F5F9", marginBottom: 14 }} />

          {/* File upload row */}
          <div style={{
            display: "flex", alignItems: "center", gap: 14,
            padding: "10px 14px",
            background: "#F8FAFC",
            border: "1.5px dashed #CBD5E1",
            borderRadius: 10,
            marginBottom:
              manualFileErrors.length > 0 || manualInputFiles.length > 0 ? 10 : 0,
          }}>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: 13, fontWeight: 600, color: "#1E293B",
                fontFamily: "Inter, sans-serif", marginBottom: 1,
              }}>
                Attach supporting files
              </div>
              <div style={{ fontSize: 11.5, color: "#94A3B8", fontFamily: "Inter, sans-serif" }}>
                PDF, Word, Excel, TXT, JPG, PNG · Max 10 MB each
              </div>
            </div>
            <label
              htmlFor="manual-ai-file-input"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "6px 14px",
                background: "#035EA5",
                color: "#FFFFFF",
                borderRadius: 8,
                fontSize: 12.5,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "Inter, sans-serif",
                whiteSpace: "nowrap",
                userSelect: "none",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#024A82")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#035EA5")}
            >
              📎 Browse
            </label>
            <input
              id="manual-ai-file-input"
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.xls,.xlsx"
              style={{ display: "none" }}
              onChange={handleManualInputFileChange}
            />
          </div>

          {/* File errors */}
          {manualFileErrors.map((err) => (
            <div key={err} style={{
              fontSize: 12, color: "#DC2626", marginTop: 4,
              fontFamily: "Inter, sans-serif",
            }}>
              {err}
            </div>
          ))}

          {/* Selected file chips */}
          {manualInputFiles.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
              {manualInputFiles.map((f) => (
                <span key={f.name} style={{
                  display: "inline-flex", alignItems: "center", gap: 5,
                  background: "#E6F0F9", border: "1px solid #BFDBFE",
                  borderRadius: 20, padding: "3px 10px",
                  fontSize: 12, color: "#035EA5", fontFamily: "Inter, sans-serif",
                }}>
                  📄 {f.name}
                  <button
                    type="button"
                    onClick={() =>
                      setManualInputFiles((prev) => prev.filter((x) => x.name !== f.name))
                    }
                    style={{
                      background: "none", border: "none", cursor: "pointer",
                      color: "#DC2626", fontSize: 14, padding: 0, lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* ── 3. Action Row ───────────────────────────────────────── */}
        <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
          <button
            type="button"
            onClick={() => {
              setManualRiskNotes("");
              setManualInputFiles([]);
              setManualFileErrors([]);
            }}
            disabled={!manualRiskNotes.trim() && manualInputFiles.length === 0}
            style={{
              padding: "8px 18px",
              borderRadius: 8,
              border: "1px solid #E2E8F0",
              background: "#F8FAFC",
              color: "#64748B",
              fontSize: 13,
              fontWeight: 600,
              cursor: !manualRiskNotes.trim() && manualInputFiles.length === 0
                ? "not-allowed" : "pointer",
              fontFamily: "Inter, sans-serif",
              opacity: !manualRiskNotes.trim() && manualInputFiles.length === 0 ? 0.5 : 1,
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => {
              if (manualRiskNotes.trim() || manualInputFiles.length > 0)
                e.currentTarget.style.background = "#F1F5F9";
            }}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#F8FAFC")}
          >
            Clear
          </button>

          <button
            type="button"
            onClick={handleSaveToQueue}
            disabled={
              addingManualRisk ||
              (!manualRiskNotes.trim() && manualInputFiles.length === 0)
            }
            style={{
              padding: "8px 22px",
              borderRadius: 8,
              border: "none",
              background:
                addingManualRisk ||
                (!manualRiskNotes.trim() && manualInputFiles.length === 0)
                  ? "#94A3B8"
                  : "linear-gradient(135deg, #6BCE8B, #4EAD6E)",
              color:
                addingManualRisk ||
                (!manualRiskNotes.trim() && manualInputFiles.length === 0)
                  ? "#fff"
                  : "#024A82",
              fontSize: 13,
              fontWeight: 700,
              cursor:
                addingManualRisk ||
                (!manualRiskNotes.trim() && manualInputFiles.length === 0)
                  ? "not-allowed" : "pointer",
              fontFamily: "Inter, sans-serif",
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              boxShadow:
                !addingManualRisk &&
                (manualRiskNotes.trim() || manualInputFiles.length > 0)
                  ? "0 2px 8px rgba(107,206,139,0.35)" : "none",
              transition: "box-shadow 0.15s, background 0.15s",
            }}
          >
            {addingManualRisk ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" />
                Saving...
              </>
            ) : (
              <>
                <span style={{ fontSize: 16, fontWeight: 800 }}>＋</span> Add to Queue
              </>
            )}
          </button>
        </div>

        {/* ── 4. "Queued & Waiting" Info Banner ───────────────────── */}
        {manualRisks.length > 0 && (
          <div style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 14,
            background: "linear-gradient(135deg, #024A82 0%, #035EA5 100%)",
            borderRadius: 12,
            padding: "14px 18px",
            marginBottom: 20,
            boxShadow: "0 2px 12px rgba(2,74,130,0.18)",
          }}>
            <div style={{
              width: 38, height: 38, borderRadius: "50%",
              background: "rgba(255,255,255,0.12)",
              border: "1.5px solid rgba(255,255,255,0.25)",
              flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              marginTop: 1,
            }}>
              <span style={{
                fontSize: 18, display: "inline-block",
                animation: "spin-slow 3s linear infinite",
              }}>
                ⚙
              </span>
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                display: "flex", alignItems: "center",
                justifyContent: "space-between", flexWrap: "wrap", gap: 8,
                marginBottom: 6,
              }}>
                <span style={{
                  fontWeight: 700, fontSize: 14, color: "#FFFFFF",
                  fontFamily: "Inter, sans-serif",
                }}>
                  {manualRisks.length} entr{manualRisks.length > 1 ? "ies" : "y"} queued &amp; waiting to be processed
                </span>
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 5,
                  background: "#FBBF24", color: "#78350F",
                  borderRadius: 20, padding: "3px 11px",
                  fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4,
                  fontFamily: "Inter, sans-serif",
                }}>
                  <span style={{
                    width: 6, height: 6, borderRadius: "50%",
                    background: "#92400E",
                    display: "inline-block",
                    animation: "pulse-dot 1.8s ease-in-out infinite",
                    flexShrink: 0,
                  }} />
                  PENDING AI PROCESSING
                </span>
              </div>

              <div style={{
                fontSize: 12.5, color: "rgba(255,255,255,0.8)",
                lineHeight: 1.6, marginBottom: 8,
                fontFamily: "Inter, sans-serif",
              }}>
                Switch to the{" "}
                <strong style={{ color: "#6BCE8B" }}>Central Risk Dashboard</strong>
                {" "}— AI reads all text &amp; file contents and extracts structured risks into
                the Risk Register.
              </div>

              <div style={{
                display: "inline-flex", alignItems: "center", gap: 7,
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.25)",
                borderRadius: 7, padding: "5px 14px",
                fontSize: 12, fontWeight: 700, color: "#FFFFFF",
                fontFamily: "Inter, sans-serif", letterSpacing: 0.2,
                cursor: "default", userSelect: "none",
              }}>
                <span style={{ fontSize: 13, color: "#6BCE8B" }}>✓</span>
                Commit &amp; Recalculate
                <span style={{
                  background: "#6BCE8B", color: "#024A82",
                  borderRadius: 10, padding: "1px 7px",
                  fontSize: 10, fontWeight: 800,
                }}>
                  {manualRisks.length} queued
                </span>
              </div>
            </div>
          </div>
        )}

        {/* ── 5. Queue Entries List ────────────────────────────────── */}
        <div style={{
          background: "#FFFFFF",
          border: "1px solid #E2E8F0",
          borderRadius: 14,
          padding: 18,
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        }}>
          <div style={{
            display: "flex", justifyContent: "space-between",
            alignItems: "center", marginBottom: 14,
          }}>
            <h6 style={{
              fontWeight: 700, color: "#0F172A", margin: 0,
              fontSize: 13.5, fontFamily: "Inter, sans-serif",
              display: "flex", alignItems: "center", gap: 8,
            }}>
              Queued Entries
              <span style={{
                padding: "2px 10px", borderRadius: 20,
                fontSize: 11, fontWeight: 700,
                background: "#E6F0F9", color: "#035EA5",
              }}>
                {manualRisks.length}
              </span>
            </h6>
            <span style={{
              fontSize: 11.5, color: "#94A3B8", fontFamily: "Inter, sans-serif",
            }}>
              Processed on next Commit &amp; Recalculate
            </span>
          </div>

          {loadingManualRisks ? (
            <div style={{
              textAlign: "center", padding: 30, color: "#94A3B8",
              fontSize: 13, fontFamily: "Inter, sans-serif",
            }}>
              <span className="spinner-border spinner-border-sm me-2" />
              Loading queue...
            </div>

          ) : manualRisks.length === 0 ? (
            <div style={{
              textAlign: "center", padding: "32px 20px",
              color: "#94A3B8", fontFamily: "Inter, sans-serif",
            }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>📋</div>
              <div style={{
                fontSize: 13.5, fontWeight: 600,
                color: "#64748B", marginBottom: 4,
              }}>
                No entries in queue
              </div>
              <div style={{ fontSize: 12.5 }}>
                Describe a risk above and click{" "}
                <strong style={{ color: "#035EA5" }}>＋ Add to Queue</strong>.
              </div>
            </div>

          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {manualRisks.map((entry, i) => (
                <li
                  key={entry.riskId || i}
                  style={{
                    borderRadius: 10,
                    border: "1px solid #E2E8F0",
                    background: "#FAFBFF",
                    padding: "12px 16px",
                    marginBottom: 10,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                    transition: "box-shadow 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.boxShadow =
                      "0 3px 10px rgba(2,74,130,0.08)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.boxShadow =
                      "0 1px 3px rgba(0,0,0,0.04)")
                  }
                >
                  {/* Number badge */}
                  <div style={{
                    width: 28, height: 28, borderRadius: "50%",
                    background: "#E6F0F9", border: "1.5px solid #BFDBFE",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 800, color: "#035EA5",
                    flexShrink: 0, marginTop: 1, fontFamily: "Inter, sans-serif",
                  }}>
                    {i + 1}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      display: "flex", alignItems: "center",
                      gap: 8, flexWrap: "wrap", marginBottom: 5,
                    }}>
                      <span style={{
                        fontSize: 11, color: "#64748B", fontWeight: 500,
                        fontFamily: "Inter, sans-serif",
                      }}>
                        Added {formatRelativeTime(entry.createdAt)}
                      </span>

                      {entry.fileNames?.length > 0 && (
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 4,
                          background: "#F0FDF4", border: "1px solid #BBF7D0",
                          color: "#15803D", borderRadius: 12,
                          padding: "1px 9px", fontSize: 11, fontWeight: 600,
                          fontFamily: "Inter, sans-serif",
                        }}>
                          📎 {entry.fileNames.length} file{entry.fileNames.length > 1 ? "s" : ""} attached
                        </span>
                      )}

                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        background: "#FFF7ED", border: "1px solid #FED7AA",
                        color: "#C2410C", borderRadius: 12,
                        padding: "1px 9px", fontSize: 11, fontWeight: 600,
                        fontFamily: "Inter, sans-serif",
                      }}>
                        ⏳ Pending
                      </span>
                    </div>

                    <div style={{
                      fontSize: 13, color: "#1E293B", lineHeight: 1.55,
                      overflow: "hidden", textOverflow: "ellipsis",
                      display: "-webkit-box", WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      marginBottom: entry.fileNames?.length > 0 ? 5 : 0,
                      fontFamily: "Inter, sans-serif",
                    }}>
                      {entry.risk || entry.notes ? (
                        entry.risk || entry.notes
                      ) : (
                        <em style={{ color: "#94A3B8" }}>No text — file(s) only</em>
                      )}
                    </div>

                    {entry.fileNames?.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 4 }}>
                        {entry.fileNames.map((fn) => (
                          <span key={fn} style={{
                            display: "inline-flex", alignItems: "center", gap: 4,
                            background: "#F1F5F9", border: "1px solid #E2E8F0",
                            color: "#475569", borderRadius: 6,
                            padding: "2px 8px", fontSize: 11,
                            fontFamily: "Inter, sans-serif",
                          }}>
                            🗒 {fn}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Delete button */}
                  <button
                    onClick={() => deleteManualRisk(entry.riskId)}
                    title="Remove entry"
                    style={{
                      background: "#FEF2F2", border: "1px solid #FECACA",
                      borderRadius: 7, cursor: "pointer",
                      color: "#DC2626", fontSize: 14,
                      width: 30, height: 30,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0, marginTop: 1,
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#FEE2E2")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "#FEF2F2")
                    }
                  >
                    🗑
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

      </div>
    </div>
  );
};

export default ManualRisk;